import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Paper,
  Stack,
  Container,
  Avatar,
  alpha,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";

/* ================= STYLES ================= */

const StyledPaper = styled(Paper)(({ theme }) => ({
  background: "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
  border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
  borderRadius: 20,
  transition: "all 0.25s ease",
  "&:hover": {
    boxShadow: "0 16px 32px rgba(3, 51, 100, 0.12)",
    transform: "translateY(-2px)",
  },
}));

/* ================= HELPERS ================= */

const confidenceColor = (confidence) => {
  switch (confidence) {
    case "HIGH":
      return "linear-gradient(135deg, #22c55e, #16a34a)";
    case "MODERATE":
      return "linear-gradient(135deg, #fbbf24, #f59e0b)";
    case "LOW":
      return "linear-gradient(135deg, #60a5fa, #3b82f6)";
    default:
      return "linear-gradient(135deg, #475569, #334155)";
  }
};

const extractEmoji = (text) => {
  const match = text.match(/\p{Emoji}/u);
  return match ? match[0] : "🧬";
};

const stripEmoji = (text) => text.replace(/[\p{Emoji}]/gu, "").trim();

/* ================= COMPONENT ================= */

function Results() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm")); // for responsiveness

  if (!state?.result) {
    return (
      <Container sx={{ py: 6, textAlign: "center" }}>
        <Typography variant="h5" fontWeight={700} color="primary">
          No Analysis Data Found
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 1, mb: 3 }}>
          Please analyze a compound to view results.
        </Typography>
        <Button variant="contained" onClick={() => navigate("/")}>
          Analyze Compound
        </Button>
      </Container>
    );
  }

  const { compound, results } = state.result;

  return (
    <Box sx={{ width: "100%", overflowX: "hidden", px: 2, py: 4 }}>
      <Grid container spacing={4} maxWidth="1200px" mx="auto">
        <Grid item xs={12}>
          {/* ================= SUMMARY ================= */}
          <StyledPaper
            sx={{
              p: 3,
              mb: 6,
              display: "flex",
              flexDirection: isSmallScreen ? "column" : "row",
              alignItems: isSmallScreen ? "center" : "center",
              justifyContent: "space-between",
              width: "100%",
              gap: 2,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: theme.palette.primary.main }}>🧪</Avatar>
              <Typography variant="h6" fontWeight={700}>
                {compound}
              </Typography>
            </Stack>

            <Box sx={{ flex: 1, textAlign: isSmallScreen ? "center" : "left" }}>
              <Typography fontWeight={700}>
                Discover Analysis Results
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Identifies the most heavily researched biological outcome based
                on PubMed keyword frequency
              </Typography>
            </Box>
          </StyledPaper>
          {/* ================= END SUMMARY ================= */}

          {/* ================= OUTCOME GROUPS ================= */}
          <Typography variant="h6" fontWeight={700} color="primary.main" mb={3}>
            Outcome Categories
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(auto-fit, minmax(300px, 1fr))",
              },
              gap: 3,
              mb: 6,
            }}
          >
            {results.map((group, idx) => (
              <StyledPaper key={idx} sx={{ minHeight: 200 }}>
                <CardContent sx={{ p: 3 }}>
                  {/* Group Header */}
                  <Stack direction="row" alignItems="center" mb={2}>
                    <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 1 }}>
                      {extractEmoji(group.group)}
                    </Avatar>
                    <Typography fontWeight={600}>
                      {stripEmoji(group.group)}
                    </Typography>
                  </Stack>

                  {/* Effects */}
                  <Stack spacing={1.2}>
                    {group.effects.map((effect, i) => (
                      <Stack
                        key={i}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: "70%",
                            whiteSpace: "normal",
                          }}
                        >
                          {effect.effect}
                        </Typography>

                        <Chip
                          size="small"
                          label={
                            effect.confidence === "NONE"
                              ? "No Evidence Found"
                              : effect.confidence || "N/A"
                          }
                          sx={{
                            fontWeight: 700,
                            height: 28,
                            px: 1.2,
                            borderRadius: "14px",
                            color: "#fff",
                            background: confidenceColor(effect.confidence),
                          }}
                        />
                      </Stack>
                    ))}
                  </Stack>
                </CardContent>
              </StyledPaper>
            ))}
          </Box>

          {/* ================= ACTION ================= */}
          {/* <Box textAlign="center" mt={isSmallScreen ? 4 : 6}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate("/biothread/discover")}
              sx={{
                px: 5,
                py: 1.5,
                fontWeight: 700,
                width: isSmallScreen ? "100%" : "auto",
              }}
            >
              Analyze New Compound
            </Button>
          </Box> */}
        </Grid>
      </Grid>
    </Box>
  );
}

export default Results;
